// ink-component
import React, { ReactNode } from 'react';
import styled from 'styled-components';

import getGlobalProps from '../../helpers/constants/global-props';

import BreadcrumbAnchor from './BreadcrumbAnchor/BreadcrumbAnchor';
import BreadcrumbButton from './BreadcrumbButton/BreadcrumbButton';
import BreadcrumbElement from './BreadcrumbElement/BreadcrumbElement';
import Box from '../Box/Box';

import { StyledAnchor } from '../Anchor/Anchor';
import { StyledButton } from '../Button/Button';

import { breadcrumbTokens } from '../../tokens/components/Breadcrumb/tokens';

export interface BreadcrumbProps extends React.HTMLAttributes<HTMLDivElement> {
  /** List of items */
  children: ReactNode;
  /** Removes bottom margin */
  trim?: boolean;
  /** Marks the element with a given ID to make it easier to select in tests */
  'data-testid'?: string | number;
}

/* @internal */
export const StyledBreadcrumb = styled.div`
  ${StyledAnchor},
  ${StyledButton} {
    &:not(:last-child):after {
      content: ' / ';
      color: ${breadcrumbTokens.FontColorBaseDefault};
      display: inline-block;
      white-space: pre;
    }
  }

  ${StyledButton} {
    display: inline-block;
  }
`;

const globalProps = getGlobalProps({});

/**
 * @purpose A list of links representing the current page and its “ancestors”
 * @group Breadcrumb
 * @categories navigation
 * @HTMLType HTMLDivElement
 */

export function Breadcrumb(props: BreadcrumbProps) {
  const { className, style, trim, ...otherProps } = props;

  return (
    <StyledBreadcrumb {...globalProps} {...otherProps}>
      {trim ? props.children : <Box bottom="24">{props.children}</Box>}
    </StyledBreadcrumb>
  );
}

Breadcrumb.Anchor = BreadcrumbAnchor;
Breadcrumb.Button = BreadcrumbButton;
Breadcrumb.Element = BreadcrumbElement;

Breadcrumb.defaultProps = {
  'data-testid': undefined,
  trim: false,
};

export { BreadcrumbAnchorProps } from './BreadcrumbAnchor/BreadcrumbAnchor';
export { BreadcrumbButtonProps } from './BreadcrumbButton/BreadcrumbButton';
export { BreadcrumbElementProps } from './BreadcrumbElement/BreadcrumbElement';

Breadcrumb.displayName = 'Breadcrumb';

export default Breadcrumb;
